import React, { FC } from 'react';
import { Box, Button, CircularProgress, Fade, Link, Switch, Typography } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { palette } from '../../theme';

export interface NotifPrefs {
  push_message_board_post: boolean;
  push_message_board_reply: boolean;
  email_message_board_post: boolean;
  push_training_uploaded: boolean;
  push_rsvp_update: boolean;
  email_platform_update: boolean;
  push_watch_data_synced: boolean;
  [key: string]: boolean;
}

export const DEFAULT_NOTIF_PREFS: NotifPrefs = {
  push_message_board_post: true,
  push_message_board_reply: true,
  email_message_board_post: false,
  push_training_uploaded: true,
  push_rsvp_update: true,
  email_platform_update: false,
  push_watch_data_synced: true,
};

interface ToggleItem {
  key: keyof NotifPrefs;
  label: string;
  description: (isGuardian: boolean) => string;
}

const TOGGLE_ITEMS: ToggleItem[] = [
  {
    key: 'push_message_board_post',
    label: 'Chat Notifications',
    description: () => 'Real-time alerts for direct messages',
  },
  {
    key: 'push_message_board_reply',
    label: 'Messages Post',
    description: () => 'Alert when new messages are posted',
  },
  {
    key: 'email_message_board_post',
    label: 'Messages Email',
    description: () => 'Receive email copy of Messages Post',
  },
  {
    key: 'push_training_uploaded',
    label: 'Workout Uploaded',
    description: () => 'When a new workout is posted',
  },
  {
    key: 'push_rsvp_update',
    label: 'Team Notifications',
    description: () => 'Schedule changes and announcements',
  },
  {
    key: 'email_platform_update',
    label: 'Weekly Summary',
    description: (isGuardian) =>
      isGuardian ? "Your athlete's training recap each week" : 'Your training recap each week',
  },
  {
    key: 'push_watch_data_synced',
    label: 'Add Workout Notes Reminder',
    description: () => 'Nudge to log notes after training',
  },
];

export interface NotificationsStepProps {
  accentColor: string;
  isGuardian: boolean;
  preferences: NotifPrefs;
  isLoading: boolean;
  onChange: (key: keyof NotifPrefs, value: boolean) => void;
  onBack: () => void;
  onEnable: () => void;
  onSkip: () => void;
}

const NotificationsStep: FC<NotificationsStepProps> = ({
  accentColor,
  isGuardian,
  preferences,
  isLoading,
  onChange,
  onBack,
  onEnable,
  onSkip,
}) => {
  const subtitle = isGuardian
    ? "Get notified about your athlete's sessions, team updates, and training reminders."
    : 'Get notified about upcoming sessions, team updates, and training reminders.';

  if (isLoading) {
    return (
      <Fade in>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 360,
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: accentColor }} />
          <Typography variant="body2" color="text.secondary">
            Saving your preferences…
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <Box>
      {/* Back link */}
      <Link
        component="button"
        onClick={onBack}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: 14,
          mb: 2.5,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
        Back
      </Link>

      {/* Icon badge */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2.5,
          backgroundColor: `${accentColor}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <NotificationsNoneIcon sx={{ color: accentColor, fontSize: 26 }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Stay in the Loop
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2.5} sx={{ lineHeight: 1.6 }}>
        {subtitle}
      </Typography>

      {/* Toggle rows */}
      <Box sx={{ mb: 2.5 }}>
        {TOGGLE_ITEMS.map((item) => (
          <Box
            key={item.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              borderBottom: `1px solid ${palette.grey[100]}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Box sx={{ flex: 1, pr: 2 }}>
              <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.3 }}>
                {item.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {item.description(isGuardian)}
              </Typography>
            </Box>
            <Switch
              checked={!!preferences[item.key]}
              onChange={(e) => onChange(item.key, e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: accentColor,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: accentColor,
                },
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Enable button */}
      <Button
        variant="contained"
        fullWidth
        onClick={onEnable}
        sx={{
          borderRadius: 999,
          py: 1.25,
          fontWeight: 600,
          fontSize: 15,
          mb: 1,
          backgroundColor: accentColor,
          boxShadow: 'none',
          '&:hover': { backgroundColor: accentColor, filter: 'brightness(0.9)', boxShadow: 'none' },
        }}
      >
        Enable Notifications
      </Button>

      {/* Not Now */}
      <Typography variant="body2" align="center">
        <Link
          component="button"
          onClick={onSkip}
          underline="none"
          sx={{ color: 'text.secondary', fontSize: 14, '&:hover': { color: 'text.primary' } }}
        >
          Not Now
        </Link>
      </Typography>
    </Box>
  );
};

export default NotificationsStep;
